import React, { useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Label, FormGroup } from 'reactstrap';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { HexColorPicker } from 'react-colorful';

import BackButton from '../../../custom/BackButton';
import ActionIcon from '../../../custom/ActionIcon';
import { createdUpdatedInfo, getDate } from '../../../../utils';
import { ISpecialHandlingCode, IUser, TEMP_SETTINGS } from '../../../../globals/interfaces';
import { Input, Textarea } from '../../../custom/hookForm/Input';
import { CheckFormGroup, CustomCheckBox } from '../../../custom/hookForm/Checkbox';
import { createSchema, updateSchema } from './utils';
import FormError from '../../../custom/FormError';
import _ from 'lodash';
import { ButtonGroup } from 'reactstrap';
import { Button } from 'reactstrap';

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    selectedItem?: ISpecialHandlingCode;
    user: IUser;
    createUpdateShc: (data: ISpecialHandlingCode, update: boolean) => Promise<void>;
    deleteShc: (id: number) => Promise<void>;
}

export default function ModalManage({
    modal,
    setModal,
    selectedItem,
    user,
    createUpdateShc,
    deleteShc
}: Props) {

    const update = selectedItem?.id !== undefined;

    const {
        register,
        handleSubmit,
        trigger,
        reset,
        watch,
        setValue,
        formState: { isValid, isDirty, errors },
    } = useForm<ISpecialHandlingCode>({
        mode: 'onTouched',
        resolver: update
            ? yupResolver(updateSchema)
            : yupResolver(createSchema),
    });

    useEffect(() => {
        const now = getDate();
        if (update) {
            selectedItem.t_modified = now;
            selectedItem.s_modified_by = user.s_email;
            reset(selectedItem);
        } else {
            reset({
                t_created: now,
                s_created_by: user.s_email,
                t_modified: now,
                s_modified_by: user.s_email
            });
        }
        trigger();
    }, [update, selectedItem, trigger, user.s_email, reset]);

    const s_card_color = watch('s_card_color');

    const toggle = () => setModal(!modal);

    const submit = (data: any) => {
        createUpdateShc(data, update);
    }

    const b_requires_refrigeration = watch('b_requires_refrigeration');
    const s_temp_setting = watch('s_temp_setting');

    useEffect(() => {
        if (!b_requires_refrigeration) {
            setValue('s_temp_setting', null);
        }
    }, [b_requires_refrigeration]);

    return (
        <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>
                        {update ? 'Update' : 'Create'} Special Handling Code
                    </h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <FormGroup>
                    <Label>Special Handling Code <FormError message={errors['s_special_handling_code']?.message as string | undefined} /> </Label>
                    <Input
                        {...register('s_special_handling_code')}
                        error={Boolean(errors['s_special_handling_code'])}
                        maxLength={3}
                    />
                </FormGroup>
                <FormGroup>
                    <CheckFormGroup>
                        <CustomCheckBox
                            type={'checkbox'}
                            {...register('b_dg')}
                        />
                        <Label check>DG</Label>
                    </CheckFormGroup>
                </FormGroup>
                <FormGroup>
                    <CheckFormGroup>
                        <CustomCheckBox
                            type={'checkbox'}
                            {...register('b_requires_refrigeration')}
                        />
                        <Label check>Requires Refrigeration</Label>
                    </CheckFormGroup>
                </FormGroup>
                {
                    b_requires_refrigeration && (
                        <SettingsContainer>
                            <Label>Temp. Setting in &deg;C</Label>
                            <ButtonGroup>
                                {
                                    TEMP_SETTINGS.map((item, i) => (
                                        <Button
                                            key={i}
                                            onClick={() => setValue('s_temp_setting', item)}
                                            active={s_temp_setting === item}
                                        >
                                            {item}&deg;C
                                        </Button>
                                    ))
                                }
                            </ButtonGroup>
                        </SettingsContainer>
                    )
                }
                <FormGroup>
                    <Label>Card Color <DisplayFormColor color={_.get(selectedItem, 's_card_color', 'white')} /></Label>
                    <Input
                        {...register('s_card_color')}
                        error={Boolean(errors['s_card_color'])}
                    />
                </FormGroup>
                <HexColorPicker
                    color={s_card_color || 'white'}
                    onChange={value => setValue('s_card_color', value, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true
                    })}
                    className={'mb-3'}
                />
                <FormGroup>
                    <Label>DG Class</Label>
                    <Textarea
                        {...register('s_dg_class')}
                        error={Boolean(errors['s_dg_class'])}
                    />
                </FormGroup>
                <FormGroup>
                    <Label>Description</Label>
                    <Textarea
                        {...register('s_description')}
                        error={Boolean(errors['s_description'])}
                    />
                </FormGroup>
                <FormGroup>
                    <Label>Example</Label>
                    <Textarea
                        {...register('s_example')}
                        error={Boolean(errors['s_example'])}
                    />
                </FormGroup>
                <FormGroup>
                    <Label>Detail</Label>
                    <Textarea
                        {...register('s_detail')}
                        error={Boolean(errors['s_detail'])}
                    />
                </FormGroup>
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    {update && (
                        <Label>{createdUpdatedInfo(selectedItem)}</Label>
                    )}
                    <FooterButtonsContainer>
                        <ActionIcon
                            type={'delete'}
                            onClick={() => selectedItem && deleteShc(selectedItem.id)}
                            disabled={!update}
                        />
                        <ActionIcon
                            type={'save'}
                            onClick={handleSubmit(submit)}
                            disabled={!isDirty || !isValid}
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </Modal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
`;

const SettingsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
`;

const DisplayFormColor = styled.div<{ color: string; }>`
    width: 10px;
    height: 10px;
    background-color: ${p => p.color || 'white'};
    border: 1px solid #000;
    display: inline-block;
`;